import React, { useEffect } from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import RecipeBuilder from "@/pages/RecipeBuilder";
import RDWorkspace from "@/pages/RDWorkspace";
import Applications from "@/pages/Applications";
import ResearchRecords from "@/pages/ResearchRecords";
import ProjectScope from "@/pages/ProjectScope";
import Ingredients from "@/pages/Ingredients";
import Regulations from "@/pages/Regulations";
import ComingSoon from "@/pages/ComingSoon";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Settings from "@/pages/Settings";
import Trust from "@/pages/Trust";
import Contact from "@/pages/Contact";
import Changelog from "@/pages/Changelog";
import ColorSwitch from "@/pages/ColorSwitch";
import PublicSpecSheet from "@/pages/PublicSpecSheet";
import AdvisorSurvey from "@/pages/AdvisorSurvey";
import Compass, { canSyncDrafts, hasPendingDraftSync, mergeDraftsWithServer } from "@/pages/Compass";
import CompassStorefront from "@/pages/CompassStorefront";
import CompassDemo from "@/pages/CompassDemo";
import Register from "@/pages/Register";
import BillingSuccess from "@/pages/BillingSuccess";
import CommercializationCenter from "@/pages/CommercializationCenter";
import Organic from "@/pages/Organic";
import DemoBanner from "@/components/DemoBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import CookieConsent from "@/components/CookieConsent";
import TechnicalReviewQueue from "@/pages/admin/TechnicalReviewQueue";
import ClientsPipeline from "@/pages/admin/ClientsPipeline";
import CrmActivation from "@/pages/CrmActivation";
import TechnicalReviewWorkspace from "@/pages/admin/TechnicalReviewWorkspace";
import "@/App.css";
import StaffRouteGuard from "@/components/StaffRouteGuard";

function KeyedBuilder() {
  const { id } = useParams();
  return <RecipeBuilder key={id} />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  if (user === false) return <Navigate to={location.pathname === "/clients" ? "/login?next=clients" : "/login"} replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function CrmOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div>Loading…</div>;
  if (!user) return <Navigate to="/login?next=clients" replace />;
  if (!["admin", "crm_staff"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

// Compass is public to purchase. Entitled users get the workspace and all
// other visitors keep the branded storefront at the same URL.
function CompassGate() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  if (user && user.has_compass) return <Compass />;
  return <CompassStorefront />;
}

// App-wide retry for a failed Compass workbook sync: if a save couldn't reach
// the server (offline) and the founder navigated to another page, push the
// pending draft as soon as the browser reports connectivity is back — no
// re-save, re-login, or return visit to /compass needed. hasPendingDraftSync
// guards against no-op merges and the merge itself is newer-wins, so a page
// that also merges (e.g. Compass on mount) never duplicates work.
function DraftSyncOnline() {
  const { user } = useAuth();
  useEffect(() => {
    if (!canSyncDrafts(user)) return undefined;
    const onOnline = () => {
      if (!hasPendingDraftSync()) return;
      mergeDraftsWithServer(user).catch(() => { /* still offline — retried on next online */ });
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [user?.id]);
  return null;
}

function Paywalled({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.has_access) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter basename={process.env.PUBLIC_URL || undefined}>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <DemoBanner />
          <DraftSyncOnline />
          <StaffRouteGuard />
          <CookieConsent />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={<ComingSoon />} />
          <Route path="/tour" element={<Navigate to="/" replace />} />
          <Route path="/dashboard" element={<Paywalled><Dashboard /></Paywalled>} />
          <Route path="/builder" element={<Paywalled><RecipeBuilder /></Paywalled>} />
          <Route path="/builder/:id" element={<Paywalled><KeyedBuilder /></Paywalled>} />
          <Route path="/rd/:id" element={<Paywalled><RDWorkspace /></Paywalled>} />
          <Route path="/applications" element={<Paywalled><Applications /></Paywalled>} />
          <Route path="/research/tests" element={<Paywalled><ResearchRecords /></Paywalled>} />
          <Route path="/research/sensory" element={<Paywalled><ResearchRecords /></Paywalled>} />
          <Route path="/research/scope" element={<Paywalled><ProjectScope /></Paywalled>} />
          <Route path="/product/:id" element={<Paywalled><CommercializationCenter /></Paywalled>} />
          <Route path="/ingredients" element={<Paywalled><Ingredients /></Paywalled>} />
          <Route path="/notes" element={<Paywalled><Regulations /></Paywalled>} />
          <Route path="/organic" element={<Paywalled><Organic /></Paywalled>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/trust" element={<Trust />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/hue" element={<Paywalled><ColorSwitch /></Paywalled>} />
          <Route path="/color-switch" element={<Navigate to="/hue" replace />} />
          <Route path="/pricing" element={<Navigate to="/" replace />} />
          <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
          <Route path="/spec/:token" element={<PublicSpecSheet />} />
          <Route path="/advisor-survey" element={<AdvisorSurvey />} />
          <Route path="/compass" element={<CompassGate />} />
          <Route path="/compass/demo" element={<CompassDemo />} />
          <Route path="/clients" element={<CrmOnly><ClientsPipeline /></CrmOnly>} />
          <Route path="/crm/activate" element={<CrmActivation />} />
          <Route path="/admin/technical-reviews" element={<AdminOnly><TechnicalReviewQueue /></AdminOnly>} />
          <Route path="/admin/technical-reviews/:id" element={<AdminOnly><TechnicalReviewWorkspace /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
